import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Folder,
  FolderOpen,
  Package,
  LayoutGrid,
  SquareStack,
  ListChecks,
  Cable,
  FileText,
  PanelsTopLeft,
  Plus,
  X,
  Image as ImageIcon,
  FileSpreadsheet,
  Save as SaveIcon,
  FolderOpen as FolderOpenIcon,
  FilePlus2,
  Trash2,
  Edit3,
  ExternalLink,
  Stamp,
} from 'lucide-react';
import { openPopoutWindow } from '../windowSync';
import { getPdfLogoDataUrl, setPdfLogoDataUrl } from '../pdfExport';
import { showToast } from './Toast';
import { useStore, type Sheet } from '../store';
import type { SheetKind } from '../types';
import {
  ConfirmModal,
  NameModal,
  PickKindModal,
  type AddDialog,
} from './SheetModals';
import { ContextMenu, type ContextMenuItem } from './ContextMenu';
import { useProjectActions } from './useProjectActions';
import { FREE_LIMITS, isPro } from '../entitlements';
import { isSupabaseConfigured } from '../supabase';

/** Same separator-aware parser used previously in SheetTabs. Kept local so
 *  this component can be lifted out without coupling. */
function parseSubsystem(name: string): { subsystem: string; localName: string } {
  let m = name.match(/^(.+?)\s*[—–]\s*(.+)$/);
  if (m) return { subsystem: m[1].trim(), localName: m[2].trim() };
  m = name.match(/^(.+?)\s+-\s*(.+)$/) || name.match(/^(.+?)\s*-\s+(.+)$/);
  if (m) return { subsystem: m[1].trim(), localName: m[2].trim() };
  return { subsystem: '', localName: name };
}

function classifySheets(
  sheets: Sheet[],
): { sheet: Sheet; subsystem: string; localName: string }[] {
  const initial = sheets.map((s) => {
    const { subsystem, localName } = parseSubsystem(s.name);
    return { sheet: s, subsystem, localName };
  });
  const knownSubs = Array.from(
    new Set(initial.filter((x) => x.subsystem).map((x) => x.subsystem)),
  ).sort((a, b) => b.length - a.length);
  for (const item of initial) {
    if (item.subsystem) continue;
    const lname = item.sheet.name.trim();
    for (const sub of knownSubs) {
      const lsub = sub.toLowerCase();
      const lsheet = lname.toLowerCase();
      if (
        lsheet === lsub ||
        lsheet.startsWith(lsub + ' ') ||
        lsheet.startsWith(lsub + '-') ||
        lsheet.startsWith(lsub + '_')
      ) {
        item.subsystem = sub;
        const tail = lname.slice(sub.length).replace(/^[\s\-_—–]+/, '').trim();
        item.localName = tail || sub;
        break;
      }
    }
  }
  return initial;
}

function iconForKind(kind: SheetKind) {
  if (kind === 'block') return LayoutGrid;
  if (kind === 'mechanical') return PanelsTopLeft;
  if (kind === 'requirements') return ListChecks;
  if (kind === 'interconnect') return Cable;
  if (kind === 'document') return FileText;
  return SquareStack;
}

const KIND_ORDER: Record<SheetKind, number> = {
  requirements: 0,
  block: 1,
  schematic: 2,
  mechanical: 3,
  interconnect: 4,
  document: 5,
};

/** Unified ghost icon-button used across the tree header — hover/active states
 *  live in .tree-hdr-btn (index.css) so all three buttons feel identical. */
const treeHdrBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  background: 'transparent',
  color: 'var(--text-muted)',
  border: '1px solid transparent',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  padding: 0,
};

const countPillStyle: CSSProperties = {
  fontSize: 9.5,
  fontWeight: 700,
  lineHeight: 1,
  color: 'var(--text-muted)',
  background: 'var(--panel-alt)',
  border: '1px solid var(--panel-border)',
  borderRadius: 999,
  padding: '2px 6px',
};

/**
 * Altium / CubeIDE-style hierarchical project view of all sheets in the
 * current project, grouped by subsystem. Selecting a sheet activates it.
 *
 * This component owns ALL sheet management UI: the "+" add menu (subsystem
 * trio, individual sheets), inline rename (double-click a row), delete (the
 * trash icon that appears on hover), and the shared confirm/name modals.
 */
export function ProjectTree({
  onCollapse,
  sectionCollapsed,
  onToggleSection,
}: {
  onCollapse?: () => void;
  sectionCollapsed?: boolean;
  onToggleSection?: () => void;
} = {}) {
  const sheets = useStore((s) => s.sheets);
  const activeSheetId = useStore((s) => s.activeSheetId);
  const setActive = useStore((s) => s.setActiveSheet);
  const renameSheet = useStore((s) => s.renameSheet);
  const addSheet = useStore((s) => s.addSheet);
  const addSubsystem = useStore((s) => s.addSubsystem);
  const addSystemOverview = useStore((s) => s.addSystemOverview);
  const deleteSheet = useStore((s) => s.deleteSheet);
  const deleteSheets = useStore((s) => s.deleteSheets);
  const projectName = useStore((s) => s.meta.name) || 'Project';
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [rootCollapsed, setRootCollapsed] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<AddDialog>(null);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);
  const [pendingDeleteSub, setPendingDeleteSub] = useState<{
    subsystem: string;
    ids: string[];
  } | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; items: ContextMenuItem[] } | null>(
    null,
  );
  const logoInputRef = useRef<HTMLInputElement>(null);

  const onPickPdfLogo = () => logoInputRef.current?.click();
  const onLogoFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      setPdfLogoDataUrl(result);
      showToast('PDF logo updated', 'success');
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };
  const onClearPdfLogo = () => {
    setPdfLogoDataUrl(null);
    showToast('PDF logo cleared', 'info');
  };

  const {
    onSave,
    onLoadClick,
    onNew,
    onExportPng,
    onExportWiring,
    onExportSpec,
    onExportPdf,
    fileInputElement,
  } = useProjectActions();

  /** Distinct subsystem folders currently in the project. */
  const subsystemCount = useMemo(() => {
    const set = new Set<string>();
    for (const s of sheets) {
      const { subsystem } = parseSubsystem(s.name);
      if (subsystem) set.add(subsystem);
    }
    return set.size;
  }, [sheets]);

  /** Open the "add subsystem" dialog — but for free accounts at the subsystem
   *  cap, show the upgrade paywall instead (this is the creation path users
   *  actually use; the store's addSubsystem guard is the backstop). */
  const requestAddSubsystem = () => {
    if (!isPro() && subsystemCount >= FREE_LIMITS.subsystems) {
      window.dispatchEvent(new CustomEvent('skema:open-upgrade'));
      return;
    }
    setDialog({ mode: 'subsystem', defaultName: 'Subsystem' });
  };

  /** All sheet ids belonging to one subsystem folder. */
  const sheetIdsInSubsystem = (subsystem: string): string[] =>
    sheets.filter((s) => parseSubsystem(s.name).subsystem === subsystem).map((s) => s.id);

  /** Add a sheet inside a specific subsystem, auto-named with the subsystem
   *  prefix and a unique counter when needed. e.g. "Robot — Schematic" or
   *  "Robot — Schematic 2" if a same-kind sheet already lives there. */
  const addSheetToSubsystem = (subsystem: string, kind: SheetKind) => {
    const baseLabel =
      kind === 'block'
        ? 'Block'
        : kind === 'schematic'
          ? 'Schematic'
          : kind === 'mechanical'
            ? 'Mechanical'
            : kind === 'requirements'
              ? 'Requirements'
              : kind === 'document'
                ? 'Document'
                : 'Harnesses';
    const sameKindCount = sheets.filter((sh) => {
      const m = sh.name.match(/^(.+?)\s*[—–-]\s*(.+)$/);
      const subSys = m ? m[1].trim() : '';
      return subSys === subsystem && sh.kind === kind;
    }).length;
    const name =
      sameKindCount === 0
        ? `${subsystem} — ${baseLabel}`
        : `${subsystem} — ${baseLabel} ${sameKindCount + 1}`;
    addSheet(kind, name);
  };

  /** Build the right-click menu for the project root. */
  const rootMenuItems = (): ContextMenuItem[] => [
    ...(isSupabaseConfigured
      ? [
          {
            label: 'My projects (cloud)…',
            onClick: () => window.dispatchEvent(new CustomEvent('skema:open-projects')),
            icon: <FolderOpen size={13} />,
          } as ContextMenuItem,
          { separator: true, label: '' } as ContextMenuItem,
        ]
      : []),
    { label: 'New project', onClick: onNew, icon: <FilePlus2 size={13} /> },
    { label: 'Open project…', onClick: onLoadClick, icon: <FolderOpenIcon size={13} /> },
    { label: 'Save project (.json)', onClick: onSave, icon: <SaveIcon size={13} /> },
    { separator: true, label: '' },
    {
      label: 'Export current sheet as PNG',
      onClick: onExportPng,
      icon: <ImageIcon size={13} />,
    },
    {
      label: 'Export Wiring (.xlsx)',
      onClick: onExportWiring,
      icon: <FileSpreadsheet size={13} />,
    },
    {
      label: 'Export Spec + BOM (.xlsx)',
      onClick: onExportSpec,
      icon: <FileSpreadsheet size={13} />,
    },
    {
      label: 'Export PDF (full drawing set)',
      onClick: onExportPdf,
      icon: <FileSpreadsheet size={13} />,
    },
    { separator: true, label: '' },
    {
      label: getPdfLogoDataUrl()
        ? 'Replace PDF title-block logo…'
        : 'Set PDF title-block logo…',
      onClick: onPickPdfLogo,
      icon: <Stamp size={13} />,
    },
    ...(getPdfLogoDataUrl()
      ? [
          {
            label: 'Remove PDF title-block logo',
            onClick: onClearPdfLogo,
            icon: <Stamp size={13} />,
            danger: true,
          } as ContextMenuItem,
        ]
      : []),
    { separator: true, label: '' },
    {
      label: 'Add subsystem…',
      onClick: () => requestAddSubsystem(),
      icon: <Plus size={13} />,
    },
    {
      label: 'Add System Overview (subsystem blocks + harness lines)',
      onClick: () => addSystemOverview(),
      icon: <LayoutGrid size={13} />,
    },
    {
      label: 'Add Harness sheet (project-wide)',
      onClick: () =>
        setDialog({ mode: 'sheet', kind: 'interconnect', defaultName: 'Harnesses' }),
      icon: <Plus size={13} />,
    },
    {
      label: 'Add Schematic (project-wide)',
      onClick: () =>
        setDialog({ mode: 'sheet', kind: 'schematic', defaultName: 'System Wiring' }),
      icon: <Plus size={13} />,
    },
    {
      label: 'Add Mechanical layout (project-wide)',
      onClick: () =>
        setDialog({ mode: 'sheet', kind: 'mechanical', defaultName: 'Mechanical Layout' }),
      icon: <Plus size={13} />,
    },
    {
      label: 'Add Document (project-wide)',
      onClick: () =>
        setDialog({ mode: 'sheet', kind: 'document', defaultName: 'Document' }),
      icon: <Plus size={13} />,
    },
  ];

  /** Build the right-click menu for a subsystem group. Each "Add …" entry
   *  drops a new sheet directly inside the subsystem with the right prefix
   *  — no more "pick the kind, then re-type the name" friction. */
  const groupMenuItems = (subsystem: string): ContextMenuItem[] => {
    if (subsystem === 'Project') {
      // The implicit "Project" group bundles project-wide sheets — those
      // come from the root menu, not from this group.
      return [
        {
          label: 'Add subsystem…',
          onClick: () => requestAddSubsystem(),
          icon: <Plus size={13} />,
        },
      ];
    }
    return [
      {
        label: `Add Block diagram to "${subsystem}"`,
        onClick: () => addSheetToSubsystem(subsystem, 'block'),
        icon: <Plus size={13} />,
      },
      {
        label: `Add Schematic to "${subsystem}"`,
        onClick: () => addSheetToSubsystem(subsystem, 'schematic'),
        icon: <Plus size={13} />,
      },
      {
        label: `Add Mechanical layout to "${subsystem}"`,
        onClick: () => addSheetToSubsystem(subsystem, 'mechanical'),
        icon: <Plus size={13} />,
      },
      {
        label: `Add Requirements to "${subsystem}"`,
        onClick: () => addSheetToSubsystem(subsystem, 'requirements'),
        icon: <Plus size={13} />,
      },
      {
        label: `Add Document to "${subsystem}"`,
        onClick: () => addSheetToSubsystem(subsystem, 'document'),
        icon: <Plus size={13} />,
      },
      { separator: true, label: '' },
      {
        label: 'Add another subsystem…',
        onClick: () => requestAddSubsystem(),
        icon: <Plus size={13} />,
      },
      { separator: true, label: '' },
      {
        label: `Delete "${subsystem}" subsystem`,
        onClick: () =>
          setPendingDeleteSub({ subsystem, ids: sheetIdsInSubsystem(subsystem) }),
        icon: <Trash2 size={13} />,
        danger: true,
      },
    ];
  };

  /** Build the right-click menu for an individual sheet. */
  const sheetMenuItems = (sheet: Sheet, canDelete: boolean): ContextMenuItem[] => {
    const isActive = sheet.id === activeSheetId;
    const exportable =
      sheet.kind === 'block' || sheet.kind === 'schematic' || sheet.kind === 'mechanical';
    const items: ContextMenuItem[] = [
      {
        label: isActive ? 'Active sheet' : 'Open',
        onClick: () => setActive(sheet.id),
        disabled: isActive,
        icon: <FolderOpenIcon size={13} />,
      },
      {
        label: 'Open in new window',
        onClick: () => openPopoutWindow(sheet.id),
        icon: <ExternalLink size={13} />,
      },
      {
        label: 'Rename…',
        onClick: () => setEditingId(sheet.id),
        icon: <Edit3 size={13} />,
      },
    ];
    if (exportable) {
      items.push({ separator: true, label: '' });
      items.push({
        label: isActive
          ? 'Export this sheet as PNG'
          : 'Activate & export as PNG',
        onClick: async () => {
          if (!isActive) {
            setActive(sheet.id);
            // Give React Flow a frame to render before grabbing the viewport.
            await new Promise((r) => requestAnimationFrame(() => r(null)));
            await new Promise((r) => requestAnimationFrame(() => r(null)));
          }
          await onExportPng();
        },
        icon: <ImageIcon size={13} />,
      });
    }
    if (sheet.kind === 'schematic' || sheet.kind === 'block') {
      items.push({
        label: 'Export Wiring (.xlsx)',
        onClick: async () => {
          if (sheet.id !== activeSheetId) setActive(sheet.id);
          // Wait a frame so the active-edges/nodes selectors update.
          await new Promise((r) => requestAnimationFrame(() => r(null)));
          await onExportWiring();
        },
        icon: <FileSpreadsheet size={13} />,
      });
    }
    items.push({ separator: true, label: '' });
    items.push({
      label: 'Delete sheet',
      onClick: () => setPendingDelete({ id: sheet.id, name: sheet.name }),
      icon: <Trash2 size={13} />,
      disabled: !canDelete,
      danger: true,
    });
    return items;
  };

  const openMenu = (e: React.MouseEvent, items: ContextMenuItem[]) => {
    e.preventDefault();
    e.stopPropagation();
    setMenu({ x: e.clientX, y: e.clientY, items });
  };

  const groups = useMemo(() => {
    const classified = classifySheets(sheets);
    const groupMap = new Map<
      string,
      { subsystem: string; sheets: Sheet[]; locals: Map<string, string> }
    >();
    for (const item of classified) {
      const key = item.subsystem || '__project__';
      if (!groupMap.has(key)) {
        groupMap.set(key, {
          subsystem: item.subsystem || 'Project',
          sheets: [],
          locals: new Map(),
        });
      }
      const g = groupMap.get(key)!;
      g.sheets.push(item.sheet);
      g.locals.set(item.sheet.id, item.localName);
    }
    for (const g of groupMap.values()) {
      g.sheets.sort((a, b) => (KIND_ORDER[a.kind] ?? 9) - (KIND_ORDER[b.kind] ?? 9));
    }
    return Array.from(groupMap.values());
  }, [sheets]);

  const toggleGroup = (subsystem: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(subsystem)) next.delete(subsystem);
      else next.add(subsystem);
      return next;
    });
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 6px 0 11px',
          background: 'linear-gradient(var(--chrome-top), var(--chrome-bottom))',
          borderBottom: '1px solid var(--chrome-border)',
          height: 30,
          boxSizing: 'border-box',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 7,
            bottom: 7,
            width: 3,
            borderRadius: '0 2px 2px 0',
            background: 'var(--accent)',
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
          {onToggleSection && (
            <button
              className="tree-hdr-btn"
              onClick={onToggleSection}
              title={sectionCollapsed ? 'Expand section' : 'Collapse section'}
              style={{ ...treeHdrBtn, width: 16, height: 16 }}
            >
              {sectionCollapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
            </button>
          )}
          <span
            style={{
              fontWeight: 700,
              fontSize: 10.5,
              letterSpacing: 0.9,
              color: 'var(--text)',
              textTransform: 'uppercase',
            }}
          >
            Project
          </span>
          <span style={countPillStyle}>{sheets.length}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isSupabaseConfigured && (
            <button
              className="tree-hdr-btn"
              onClick={() => window.dispatchEvent(new CustomEvent('skema:open-projects'))}
              title="My projects — open a project saved to your account"
              style={treeHdrBtn}
            >
              <FolderOpen size={14} />
            </button>
          )}
          {onCollapse && (
            <button
              className="tree-hdr-btn"
              onClick={onCollapse}
              title="Collapse panel"
              style={treeHdrBtn}
            >
              <ChevronLeft size={14} />
            </button>
          )}
          <button
            className="tree-hdr-btn tree-hdr-btn--primary"
            onClick={() => requestAddSubsystem()}
            title="Add a new subsystem (creates Requirements + Block + Schematic). Right-click on a subsystem to add individual sheets."
            style={treeHdrBtn}
          >
            <Plus size={15} />
          </button>
        </div>
      </div>

      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '4px 0',
          fontSize: 12,
        }}
      >
        {/* Project root row */}
        <Row
          depth={0}
          icon={<Package size={14} />}
          chevron={
            rootCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />
          }
          label={projectName}
          bold
          onClick={() => setRootCollapsed((v) => !v)}
          onContextMenu={(e) => openMenu(e, rootMenuItems())}
        />

        {!rootCollapsed &&
          groups.map((group) => {
            const isCollapsed = collapsed.has(group.subsystem);
            const FolderIcon = isCollapsed ? Folder : FolderOpen;
            return (
              <div key={group.subsystem}>
                <Row
                  depth={1}
                  icon={<FolderIcon size={13} />}
                  chevron={
                    isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />
                  }
                  label={group.subsystem}
                  count={group.sheets.length}
                  onClick={() => toggleGroup(group.subsystem)}
                  onContextMenu={(e) => openMenu(e, groupMenuItems(group.subsystem))}
                />
                {!isCollapsed &&
                  group.sheets.map((sheet) => {
                    const Icon = iconForKind(sheet.kind);
                    const isActive = sheet.id === activeSheetId;
                    const localName = group.locals.get(sheet.id) ?? sheet.name;
                    const sameKindCount = sheets.filter(
                      (sh) => sh.kind === sheet.kind,
                    ).length;
                    const canDelete = sameKindCount > 1;
                    return (
                      <SheetRow
                        key={sheet.id}
                        depth={2}
                        sheet={sheet}
                        displayName={localName}
                        icon={<Icon size={12} />}
                        active={isActive}
                        editing={editingId === sheet.id}
                        canDelete={canDelete}
                        onSelect={() => setActive(sheet.id)}
                        onStartEdit={() => setEditingId(sheet.id)}
                        onCommitEdit={(name) => {
                          if (name.trim()) renameSheet(sheet.id, name.trim());
                          setEditingId(null);
                        }}
                        onCancelEdit={() => setEditingId(null)}
                        onRequestDelete={() =>
                          setPendingDelete({ id: sheet.id, name: sheet.name })
                        }
                        onContextMenu={(e) =>
                          openMenu(e, sheetMenuItems(sheet, canDelete))
                        }
                      />
                    );
                  })}
              </div>
            );
          })}
      </div>

      {fileInputElement}

      {/* Hidden image picker for the PDF title-block logo */}
      <input
        ref={logoInputRef}
        type="file"
        accept="image/png,image/jpeg,image/svg+xml,image/webp"
        style={{ display: 'none' }}
        onChange={onLogoFile}
      />

      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          items={menu.items}
          onClose={() => setMenu(null)}
        />
      )}

      {/* Modals */}
      {dialog?.mode === 'pickKind' && (
        <PickKindModal
          onCancel={() => setDialog(null)}
          onPick={(kind) => {
            if (kind === 'subsystem') {
              requestAddSubsystem();
              return;
            }
            const defaultName =
              kind === 'block'
                ? 'Block Diagram'
                : kind === 'mechanical'
                  ? 'Mechanical Layout'
                  : kind === 'requirements'
                    ? 'Requirements'
                    : kind === 'interconnect'
                      ? 'Harnesses'
                      : kind === 'document'
                        ? 'Maintenance Manual'
                        : 'Schematic';
            setDialog({ mode: 'sheet', kind, defaultName });
          }}
        />
      )}

      {dialog?.mode === 'subsystem' && (
        <NameModal
          title="Add subsystem"
          description={
            'Adds a Requirements + Block + Schematic sheet trio to the current project.\n\nExamples: "Robot", "Control Box", "Sensor Module".'
          }
          defaultValue={dialog.defaultName}
          okLabel="Add subsystem"
          onCancel={() => setDialog(null)}
          onSubmit={(name) => {
            addSubsystem(name);
            setDialog(null);
          }}
        />
      )}

      {dialog?.mode === 'sheet' && (
        <NameModal
          title={`Add ${dialog.kind} sheet`}
          description={
            dialog.kind === 'document'
              ? 'A free-form text sheet for installation guides, maintenance manuals, troubleshooting steps, etc.'
              : undefined
          }
          defaultValue={dialog.defaultName}
          onCancel={() => setDialog(null)}
          onSubmit={(name) => {
            addSheet(dialog.kind, name);
            setDialog(null);
          }}
        />
      )}

      {pendingDelete && (
        <ConfirmModal
          title="Delete sheet"
          message={`Delete sheet "${pendingDelete.name}"? This cannot be undone.`}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => {
            deleteSheet(pendingDelete.id);
            setPendingDelete(null);
          }}
        />
      )}
      {pendingDeleteSub && (
        <ConfirmModal
          title="Delete subsystem"
          message={`Delete the "${pendingDeleteSub.subsystem}" subsystem and all ${pendingDeleteSub.ids.length} of its sheets? This cannot be undone.`}
          onCancel={() => setPendingDeleteSub(null)}
          onConfirm={() => {
            deleteSheets(pendingDeleteSub.ids);
            setPendingDeleteSub(null);
            showToast(`Deleted subsystem "${pendingDeleteSub.subsystem}"`, 'info');
          }}
        />
      )}
    </div>
  );
}

interface RowProps {
  depth: number;
  icon: React.ReactNode;
  chevron?: React.ReactNode;
  label: string;
  count?: number;
  active?: boolean;
  bold?: boolean;
  title?: string;
  onClick?: () => void;
  onDoubleClick?: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
}

function Row({
  depth,
  icon,
  chevron,
  label,
  count,
  active,
  bold,
  title,
  onClick,
  onDoubleClick,
  onContextMenu,
}: RowProps) {
  return (
    <div
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      title={title ?? label}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        padding: '4px 10px 4px 0',
        paddingLeft: 10 + depth * 15,
        cursor: onClick ? 'pointer' : 'default',
        background: active ? 'var(--accent-soft)' : 'transparent',
        color: active ? 'var(--accent)' : 'var(--text)',
        fontWeight: active || bold ? 700 : 500,
        userSelect: 'none',
        borderLeft: active ? '2px solid var(--accent)' : '2px solid transparent',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = 'var(--panel-alt)';
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = 'transparent';
      }}
    >
      <span
        style={{
          width: 12,
          display: 'inline-flex',
          justifyContent: 'center',
          flexShrink: 0,
          color: active ? 'var(--accent)' : 'var(--text-dim)',
        }}
      >
        {chevron}
      </span>
      <span
        style={{
          flexShrink: 0,
          display: 'inline-flex',
          color: active ? 'var(--accent)' : 'var(--text-muted)',
        }}
      >
        {icon}
      </span>
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </span>
      {count != null && <span style={rowCountStyle}>{count}</span>}
    </div>
  );
}

/** Small muted count badge shown on subsystem folder rows. */
const rowCountStyle: CSSProperties = {
  fontSize: 9.5,
  fontWeight: 700,
  lineHeight: 1,
  color: 'var(--text-muted)',
  background: 'var(--panel-alt)',
  border: '1px solid var(--panel-border)',
  borderRadius: 999,
  padding: '2px 6px',
  flexShrink: 0,
};

interface SheetRowProps {
  depth: number;
  sheet: Sheet;
  displayName: string;
  icon: React.ReactNode;
  active: boolean;
  editing: boolean;
  canDelete: boolean;
  onSelect: () => void;
  onStartEdit: () => void;
  onCommitEdit: (name: string) => void;
  onCancelEdit: () => void;
  onRequestDelete: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
}

/** Sheet leaf row — adds inline rename + hover-only delete button on top of
 *  the basic Row visuals. Double-click to rename, single-click to activate. */
function SheetRow({
  depth,
  sheet,
  displayName,
  icon,
  active,
  editing,
  canDelete,
  onSelect,
  onStartEdit,
  onCommitEdit,
  onCancelEdit,
  onRequestDelete,
  onContextMenu,
}: SheetRowProps) {
  const [hovered, setHovered] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  return (
    <div
      onClick={editing ? undefined : onSelect}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onStartEdit();
      }}
      onContextMenu={onContextMenu}
      onMouseEnter={(e) => {
        setHovered(true);
        if (!active) e.currentTarget.style.background = 'var(--panel-alt)';
      }}
      onMouseLeave={(e) => {
        setHovered(false);
        if (!active) e.currentTarget.style.background = 'transparent';
      }}
      title={editing ? '' : `${sheet.name} — double-click to rename`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        padding: '4px 6px 4px 0',
        paddingLeft: 10 + depth * 15,
        cursor: editing ? 'text' : 'pointer',
        background: active ? 'var(--accent-soft)' : 'transparent',
        color: active ? 'var(--accent)' : 'var(--text)',
        fontWeight: active ? 700 : 500,
        userSelect: 'none',
        borderLeft: active ? '2px solid var(--accent)' : '2px solid transparent',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
      }}
    >
      <span
        style={{
          width: 12,
          flexShrink: 0,
          display: 'inline-flex',
          justifyContent: 'center',
        }}
      />
      <span
        style={{
          flexShrink: 0,
          display: 'inline-flex',
          color: active ? 'var(--accent)' : 'var(--text-muted)',
        }}
      >
        {icon}
      </span>
      {editing ? (
        <input
          ref={inputRef}
          defaultValue={sheet.name}
          onClick={(e) => e.stopPropagation()}
          onBlur={(e) => {
            const v = e.target.value.trim();
            if (v) onCommitEdit(v);
            else onCancelEdit();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            else if (e.key === 'Escape') onCancelEdit();
            e.stopPropagation();
          }}
          style={{
            flex: 1,
            background: 'var(--input-bg, var(--panel))',
            border: '1px solid var(--accent)',
            borderRadius: 3,
            padding: '1px 4px',
            fontSize: 12,
            color: 'var(--text)',
            outline: 'none',
            minWidth: 0,
          }}
        />
      ) : (
        <span
          style={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {displayName}
        </span>
      )}
      {canDelete && hovered && !editing && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRequestDelete();
          }}
          title={`Delete "${sheet.name}"`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 18,
            height: 18,
            border: 'none',
            background: 'transparent',
            color: 'var(--text-muted)',
            cursor: 'pointer',
            padding: 0,
            borderRadius: 3,
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--danger)';
            e.currentTarget.style.color = 'white';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = 'var(--text-muted)';
          }}
        >
          <X size={12} />
        </button>
      )}
    </div>
  );
}
